/**
 * Top progress bar — "loading bar" tipis di atas layar.
 *
 * Tujuan UX: saat user mengetuk tombol/menu dan jaringan lambat, halaman bisa
 * terasa "freeze" (belum berubah). Bar ini memberi sinyal "sedang memuat" supaya
 * user tahu ketukannya diterima dan sistem sedang bekerja.
 *
 * Modul ini = singleton "bus" tanpa dependensi runtime. Komponen visual
 * (`TopLoader.tsx`) berlangganan ke bus; navigasi (klik link / `router.push`) dan
 * aksi async apa pun bisa menyalakan bar lewat API di bawah.
 *
 * Aturan aktif = `navigating || tasks > 0`. `nextTrickle` murni & diuji.
 */

type LoaderListener = (active: boolean) => void;

const listeners = new Set<LoaderListener>();

/** Jumlah aksi async eksplisit yang sedang berjalan (start/done/run). */
let tasks = 0;
/** Apakah sebuah navigasi rute sedang berlangsung. */
let navigating = false;
/** Pengaman: paksa selesai bila navigasi tak pernah "mendarat". */
let navTimeout: ReturnType<typeof setTimeout> | null = null;

const NAV_SAFETY_MS = 12_000;

function isActive(): boolean {
  return navigating || tasks > 0;
}

function emit(): void {
  const active = isActive();
  listeners.forEach((listener) => listener(active));
}

function clearNavTimeout(): void {
  if (navTimeout) {
    clearTimeout(navTimeout);
    navTimeout = null;
  }
}

export const topLoader = {
  /** Berlangganan perubahan status aktif. Mengembalikan fungsi unsubscribe. */
  subscribe(listener: LoaderListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Mulai satu aksi async (mis. sebelum `await fetch`). Wajib dipasangkan `done()`. */
  start(): void {
    tasks += 1;
    emit();
  },

  /** Akhiri satu aksi async. Aman dipanggil berlebih (tidak pernah < 0). */
  done(): void {
    tasks = Math.max(0, tasks - 1);
    emit();
  },

  /**
   * Bungkus sebuah promise agar bar otomatis menyala selama promise berjalan,
   * dan padam saat selesai (baik resolve maupun reject).
   */
  run<T>(promise: Promise<T>): Promise<T> {
    topLoader.start();
    return promise.finally(() => {
      topLoader.done();
    });
  },

  /** Tandai sebuah navigasi rute dimulai (dipanggil saat klik link / pushState). */
  startNavigation(): void {
    navigating = true;
    clearNavTimeout();
    navTimeout = setTimeout(() => {
      navigating = false;
      navTimeout = null;
      emit();
    }, NAV_SAFETY_MS);
    emit();
  },

  /** Tandai navigasi rute selesai (dipanggil saat pathname/search berubah). */
  finishNavigation(): void {
    clearNavTimeout();
    if (navigating) {
      navigating = false;
      emit();
    }
  },

  /** Reset penuh — dipakai pengujian. */
  reset(): void {
    clearNavTimeout();
    tasks = 0;
    navigating = false;
    emit();
  },

  /** Status aktif terkini (dipakai pengujian / debug). */
  isActive,
};

/**
 * Hitung nilai progres berikutnya saat "menetes" (trickle) menuju ~99%.
 *
 * Bar tak pernah mencapai 100% sendiri — hanya `done`/`finishNavigation` yang
 * menutupnya ke penuh. Langkah mengecil saat mendekati ujung supaya terasa
 * "masih jalan" tanpa pernah benar-benar selesai sebelum waktunya.
 *
 * @param current progres saat ini dalam rentang [0, 1)
 * @returns progres berikutnya, dibatasi maksimum 0.994
 */
export function nextTrickle(current: number): number {
  const CAP = 0.994;
  if (current >= CAP) return CAP;

  let step: number;
  if (current < 0.2) step = 0.1;
  else if (current < 0.5) step = 0.045;
  else if (current < 0.8) step = 0.02;
  else step = 0.008;

  return Math.min(current + step, CAP);
}
